import { PrismButton, PrismCheckbox, PrismDialog, PrismInputText, PrismInputTextarea } from "@prism-ui/react";
import type { EventDraft } from "./ProjectFairs";
import { usePreservingToggle } from "./usePreservingToggle";

interface ClosingBannerModalProps {
  show: boolean;
  draft: EventDraft;
  saving: boolean;
  onChange: (updater: (prev: EventDraft) => EventDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function ClosingBannerModal({ show, draft, saving, onChange, onSave, onCancel }: ClosingBannerModalProps) {
  const [channelEnabled, toggleChannel] = usePreservingToggle({
    open: show,
    initiallyOn: !!(draft.support_channel_name || draft.support_channel_link),
    snapshot: () => ({ name: draft.support_channel_name, link: draft.support_channel_link }),
    clear: () => onChange((prev) => ({ ...prev, support_channel_name: "", support_channel_link: "" })),
    restore: (stashed: { name: string; link: string }) =>
      onChange((prev) => ({ ...prev, support_channel_name: stashed.name, support_channel_link: stashed.link })),
  });
  const [feedbackEnabled, toggleFeedback] = usePreservingToggle({
    open: show,
    initiallyOn: !!(draft.survey_text || draft.survey_link),
    snapshot: () => ({ text: draft.survey_text, link: draft.survey_link }),
    clear: () => onChange((prev) => ({ ...prev, survey_text: "", survey_link: "" })),
    restore: (stashed: { text: string; link: string }) =>
      onChange((prev) => ({ ...prev, survey_text: stashed.text, survey_link: stashed.link })),
  });

  return (
    <PrismDialog open={show} close-method="user" onPrismClose={onCancel} setAriaLabel="Closing Banner">
      <span slot="header">Closing Banner</span>
      <div className="project-fairs-modal-body">
        <section className="project-fairs-tile">
          <h4>Join Our Channel</h4>
          <p className="project-fairs-field-help">
            Optionally provide a Teams channel for attendees who need help opening an individual invite.
          </p>
          <PrismCheckbox
            label="Show the join channel tile"
            checked={channelEnabled}
            onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => toggleChannel(!!e.detail?.checked)}
          />
          {channelEnabled ? (
            <div className="project-fairs-tile-fields">
              <div className="project-fairs-grid">
                <PrismInputText
                  label="Channel Name"
                  value={draft.support_channel_name}
                  hint="Used as the link text in the sentence below."
                  onPrismInput={(e: CustomEvent<{ value: string }>) =>
                    onChange((prev) => ({ ...prev, support_channel_name: e.detail?.value || "" }))
                  }
                />
                <PrismInputText
                  label="Channel Link"
                  value={draft.support_channel_link}
                  hint="Required — without it the tile is not shown at all."
                  onPrismInput={(e: CustomEvent<{ value: string }>) =>
                    onChange((prev) => ({ ...prev, support_channel_link: e.detail?.value || "" }))
                  }
                />
              </div>
              {/* The two fields above supply only the link text; the sentence wrapped around
                  them is fixed in the public page and is invisible from here. Showing the
                  assembled sentence is what makes the name field's role obvious — and it is
                  the only place the "Project Fair" fallback is discoverable without either
                  reading the source or entering preview mode. */}
              <div className="project-fairs-sentence-preview">
                <span className="project-fairs-field-help">Attendees will see:</span>
                <p>
                  Need help getting into an invite? Join the{" "}
                  <a
                    href={draft.support_channel_link || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.preventDefault()}
                  >
                    {draft.support_channel_name || "Project Fair"}
                  </a>{" "}
                  channel.
                </p>
                {!draft.support_channel_name ? (
                  <p className="project-fairs-field-help">
                    No channel name set, so the link reads &ldquo;Project Fair&rdquo;.
                  </p>
                ) : null}
                {!draft.support_channel_link ? (
                  <p className="project-fairs-field-help">
                    Add a channel link to make this tile appear on the public page.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>
        <section className="project-fairs-tile">
          <h4>Thank You Message</h4>
          <PrismInputTextarea
            label="Closing Message"
            rows={4}
            value={draft.closing_msg}
            onPrismInput={(e: CustomEvent<{ value: string }>) =>
              onChange((prev) => ({ ...prev, closing_msg: e.detail?.value || "" }))
            }
          />
        </section>
        <section className="project-fairs-tile">
          <h4>Provide Feedback</h4>
          <PrismCheckbox
            label="Show the feedback tile"
            checked={feedbackEnabled}
            onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => toggleFeedback(!!e.detail?.checked)}
          />
          {feedbackEnabled ? (
            <div className="project-fairs-tile-fields">
              <PrismInputTextarea
                label="Survey Text"
                rows={3}
                value={draft.survey_text}
                hint="This becomes the link text itself — there is no separate label."
                onPrismInput={(e: CustomEvent<{ value: string }>) =>
                  onChange((prev) => ({ ...prev, survey_text: e.detail?.value || "" }))
                }
              />
              <PrismInputText
                label="Survey Link"
                value={draft.survey_link}
                hint="Both the text and the link are required for this tile to appear."
                onPrismInput={(e: CustomEvent<{ value: string }>) =>
                  onChange((prev) => ({ ...prev, survey_link: e.detail?.value || "" }))
                }
              />
            </div>
          ) : null}
        </section>
      </div>
      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onCancel}>
        Cancel
      </PrismButton>
      <PrismButton slot="footer" tone="primary-brand" display="fill" disabled={saving} loading={saving} onPrismClick={onSave}>
        Save
      </PrismButton>
    </PrismDialog>
  );
}
